// Shared task sorting used by every TaskManagement view (List / Table / Kanban).
//
// The three views carry tasks in slightly different shapes: the List/Kanban
// tree uses camelCase keys (`lineUp`, `dueDate`) while the Table uses the
// model's snake_case keys (`line_up`, `do_date`). The helpers below read a
// single logical field across both shapes so one sort spec drives all views.

/**
 * Fields offered in the sort menu. `key` is the logical field; the readers
 * below resolve it against whichever shape a task happens to use.
 */
export const SORT_FIELDS = [
  { key: "line_up", label: "Line Up" },
  { key: "do_date", label: "Do Date" },
  { key: "status", label: "Status" },
  { key: "name", label: "Name" },
  { key: "created_at", label: "Created At" },
  { key: "updated_at", label: "Modified At" },
];

// Fields parsed as dates when comparing.
const DATE_FIELDS = new Set(["do_date", "created_at", "updated_at"]);

// Logical field -> the concrete keys it can live under, in priority order.
const FIELD_ALIASES = {
  line_up: ["line_up", "lineUp"],
  do_date: ["do_date", "dueDate"],
};

// Workflow order so a Status sort follows the board left-to-right, not A→Z.
const STATUS_ORDER = {
  "to start": 1,
  doing: 2,
  "on hold": 3,
  "task complete": 4,
  documented: 5,
  "closed w review": 6,
};

const NUMERIC_FIELDS = new Set(["line_up"]);

const getValue = (task, field) => {
  const keys = FIELD_ALIASES[field] || [field];
  for (const k of keys) {
    if (task[k] !== undefined && task[k] !== null) return task[k];
  }
  return null;
};

/**
 * Compares two tasks against an ordered list of sort criteria.
 * @param {Object} a
 * @param {Object} b
 * @param {{field: string, dir: ('asc'|'desc')}[]} criteria
 * @returns {number}
 */
export const compareTasks = (a, b, criteria) => {
  for (const { field, dir } of criteria) {
    const mul = dir === "desc" ? -1 : 1;
    let av = getValue(a, field);
    let bv = getValue(b, field);

    if (av == null && bv == null) continue;
    if (av == null) return 1; // nulls sink regardless of direction
    if (bv == null) return -1;

    if (DATE_FIELDS.has(field)) {
      av = new Date(av).getTime();
      bv = new Date(bv).getTime();
    } else if (field === "status") {
      av = STATUS_ORDER[String(av).toLowerCase()] || 999;
      bv = STATUS_ORDER[String(bv).toLowerCase()] || 999;
    } else if (NUMERIC_FIELDS.has(field)) {
      av = Number(av) || 0;
      bv = Number(bv) || 0;
    } else {
      av = String(av).toLowerCase();
      bv = String(bv).toLowerCase();
    }

    if (av < bv) return -1 * mul;
    if (av > bv) return 1 * mul;
  }
  return 0;
};

/**
 * Returns a new array of tasks sorted by the criteria. Returns the input
 * untouched when there is nothing to sort by.
 * @param {Object[]} tasks
 * @param {{field: string, dir: ('asc'|'desc')}[]} criteria
 * @returns {Object[]}
 */
export const sortTasks = (tasks, criteria) => {
  if (!Array.isArray(tasks) || !criteria || criteria.length === 0) return tasks;
  return [...tasks].sort((a, b) => compareTasks(a, b, criteria));
};

/**
 * Sorts the tasks *within* each track of the nested project tree, leaving the
 * project/track order intact. Used by the List and Kanban views so the shared
 * sort spec reorders tasks in place.
 * @param {Array} projects - Nested projects → tracks → tasks
 * @param {{field: string, dir: ('asc'|'desc')}[]} criteria
 * @returns {Array}
 */
export const sortProjectTree = (projects, criteria) => {
  if (!Array.isArray(projects) || !criteria || criteria.length === 0) {
    return projects;
  }
  return projects.map((project) => ({
    ...project,
    tracks: (project.tracks || []).map((track) => ({
      ...track,
      tasks: sortTasks(track.tasks || [], criteria),
    })),
    tasks: project.tasks ? sortTasks(project.tasks, criteria) : project.tasks,
  }));
};
